import { stripTypename } from "@apollo/client/utilities";
import type { EventInput } from "@fullcalendar/core/index.js";
import {
  type CreateScheduledEventMutationVariables,
  type ScheduledEvent,
  ScheduledEventModificationType,
  type ScheduledEventRecurringSettings,
  type UpdateScheduledEventMutationVariables,
} from "@package/ui-gql/index-data-access";
import moment from "moment";
import { Frequency, type RRule } from "rrule";

export function convertScheduledEventsToCalendarInputs(
  events?: ScheduledEvent[],
  selectedEventId?: string,
): EventInput[] {
  if (!Array.isArray(events)) {
    return [];
  }

  return events.map((event) => ({
    id: event.id ?? undefined,
    start: new Date(event.start),
    end: new Date(event.end),
    groupId: event.groupId ?? undefined,
    title: event.name ?? undefined,
    display: event.notes ?? undefined,
    backgroundColor: selectedEventId === event.id ? "#6ba7e3" : "#3788d8",
    rrule: event.recurring
      ? {
          byweekday: event.recurring?.byweekday ?? undefined,
          bymonth: event.recurring?.bymonth ?? undefined,
          count: event.recurring?.count ?? undefined,
          dtstart: event.recurring?.dtstart
            ? moment(event.recurring?.dtstart)?.toDate()
            : new Date(),
          freq: event.recurring?.freq ?? undefined,
          interval: event.recurring?.interval ?? undefined,
          tzid: event.recurring?.tzid ?? undefined,
          until: event.recurring?.until ?? undefined,
        }
      : undefined,
  }));
}

export function createScheduledEventRecurringSettingsFromRRule(
  rrule: RRule,
): ScheduledEventRecurringSettings {
  return {
    bymonth: rrule.options.bymonth,
    byweekday: rrule.options.byweekday,
    count: rrule.options.count,
    dtstart: rrule.options.dtstart.toISOString(),
    freq: rrule.options.freq,
    interval: rrule.options.interval,
    tzid: rrule.options.tzid,
    until: rrule.options.until?.toISOString(),
  };
}

export function createScheduledEventMutationVariables(
  event: ScheduledEvent,
): CreateScheduledEventMutationVariables {
  return {
    ...stripTypename(event),
    ...adjustScheduledEventStartEndActions(event),
    name: event.name ?? "Not Set",
    start: event.start,
    recurring: event.recurring
      ? {
          ...event.recurring,
          freq: event.recurring.freq ?? Frequency.DAILY,
          interval: event.recurring.interval ?? 1,
          count: event.recurring.count ?? 1000,
        }
      : undefined,
  };
}

export function updateScheduledEventMutationVariables(
  event: ScheduledEvent,
  modifyType: ScheduledEventModificationType = ScheduledEventModificationType.Single,
): UpdateScheduledEventMutationVariables {
  return {
    ...stripTypename(event),
    ...adjustScheduledEventStartEndActions(event),
    id: event.id ?? "",
    name: event.name ?? "Not Set",
    modifyType,
    start: event.start,
  };
}

function adjustScheduledEventStartEndActions(
  event: ScheduledEvent,
):
  | Partial<CreateScheduledEventMutationVariables>
  | Partial<UpdateScheduledEventMutationVariables> {
  return {
    startActionOneOf:
      event.startAction?.map((action) => ({
        type: action.type,
        oneOf: {
          [action.type]: stripTypename({
            ...action,
            type: undefined,
          }),
        },
      })) ?? undefined,
    endActionOneOf:
      event.endAction?.map((action) => ({
        type: action.type,
        oneOf: {
          [action.type]: stripTypename({
            ...action,
            type: undefined,
          }),
        },
      })) ?? undefined,
  };
}
